import React, { useState } from 'react';
import { PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '../../contexts/StoreContext';
import { Modal } from '../ui/Modal';
import { Field } from '../ui/Field';
import { Banner, BannerKind } from '../../types/admin';
import { formatDate, isWithin } from '../../utils/format';
const kinds: BannerKind[] = ['Lanzamiento', 'Oferta', 'Novedad', 'Campaña'];
function BannerEditor({
  banner,
  onDone
}: {
  banner: Banner | null;
  onDone: () => void;
}) {
  const {
    saveBanner
  } = useStore();
  const today = new Date().toISOString().slice(0, 10);
  const [d, setD] = useState<Banner>(banner ?? {
    id: `banner-${Date.now().toString(36)}`,
    kind: 'Novedad',
    title: '',
    subtitle: '',
    ctaLabel: 'Ver más',
    link: '/catalogo',
    image: '',
    active: true,
    startsAt: today,
    endsAt: today
  });
  const [error, setError] = useState('');
  const set = <K extends keyof Banner,>(k: K, v: Banner[K]) => setD(x => ({
    ...x,
    [k]: v
  }));
  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!d.title.trim()) return setError('Ingresá un título.');
    if (!/^https:\/\//.test(d.image)) return setError('La imagen debe ser una URL https.');
    if (!d.link.startsWith('/')) return setError('El enlace debe ser una ruta interna (ej.: /catalogo?body=SUV).');
    if (d.endsAt < d.startsAt) return setError('La fecha de fin debe ser posterior al inicio.');
    saveBanner(d);
    toast.success('Banner guardado');
    onDone();
  };
  return <form onSubmit={save} className="space-y-4" noValidate data-id="element-32" data-mp="34fkvmu1xrbog">
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-33" data-mp="34h5psikc8sls">
        <Field label="Tipo" htmlFor="bn-kind" data-id="element-34" data-mp="3ifvq072gf8ed">
          <select id="bn-kind" className="input" value={d.kind} onChange={e => set('kind', e.target.value as BannerKind)} data-id="element-35" data-mp="3icq2uxddygla">
            {kinds.map(k => <option key={k} data-id="element-36" data-mp="xntvjnjgtobs">{k}</option>)}
          </select>
        </Field>
        <Field label="Texto del botón" htmlFor="bn-cta" data-id="element-37" data-mp="tna0qqmd6wst">
          <input id="bn-cta" className="input" value={d.ctaLabel} onChange={e => set('ctaLabel', e.target.value)} maxLength={24} data-id="element-38" data-mp="tmgtlojhu5a5" />
        </Field>
      </div>
      <Field label="Título" htmlFor="bn-title" data-id="element-39" data-mp="3rg1vnr21ay1e">
        <input id="bn-title" className="input" value={d.title} onChange={e => set('title', e.target.value)} maxLength={80} data-id="element-40" data-mp="3reqmtctqf03f" />
      </Field>
      <Field label="Bajada" htmlFor="bn-sub" data-id="element-41" data-mp="4cdos2h63uaf">
        <input id="bn-sub" className="input" value={d.subtitle} onChange={e => set('subtitle', e.target.value)} maxLength={140} data-id="element-42" data-mp="4bc3l56ftrez" />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-43" data-mp="38ptz5rsub5u6">
        <Field label="Enlace interno" htmlFor="bn-link" data-id="element-44" data-mp="38qlio495xd43">
          <input id="bn-link" className="input" value={d.link} onChange={e => set('link', e.target.value)} data-id="element-45" data-mp="3cr5g78xneiqr" />
        </Field>
        <Field label="Imagen (URL https)" htmlFor="bn-img" data-id="element-46" data-mp="3hj72lnoj5pui">
          <input id="bn-img" className="input" value={d.image} onChange={e => set('image', e.target.value)} data-id="element-47" data-mp="2l0r7rs8elymh" />
        </Field>
        <Field label="Desde" htmlFor="bn-start" data-id="element-48" data-mp="2p11ex87xmv2x">
          <input id="bn-start" type="date" className="input" value={d.startsAt} onChange={e => set('startsAt', e.target.value)} data-id="element-49" data-mp="2ozqad66nxjar" />
        </Field>
        <Field label="Hasta" htmlFor="bn-end" data-id="element-50" data-mp="2ckjx4642y6pt">
          <input id="bn-end" type="date" className="input" value={d.endsAt} onChange={e => set('endsAt', e.target.value)} data-id="element-51" data-mp="2chnyefkere0b" />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm" data-id="element-52" data-mp="23hr520n2pqyx">
        <input type="checkbox" checked={d.active} onChange={e => set('active', e.target.checked)} className="h-4 w-4 accent-[#1FA8FF]" data-id="element-53" data-mp="1yjpkfba01jlc" /> Activo
      </label>
      {error && <p className="text-sm text-danger" role="alert" data-id="element-54" data-mp="1trocg1jv6f14">{error}</p>}
      <div className="flex justify-end gap-2" data-id="element-55" data-mp="1ouxypkvj7viv">
        <button type="button" onClick={onDone} className="btn btn-ghost" data-id="element-56" data-mp="1ortj7rcm9dcw">Cancelar</button>
        <button type="submit" className="btn btn-primary" data-id="element-57" data-mp="1kq821tt1ucww">Guardar</button>
      </div>
    </form>;
}
export function BannersPanel() {
  const {
    banners,
    deleteBanner
  } = useStore();
  const [editing, setEditing] = useState<Banner | null | 'new'>(null);
  return <div data-id="element-58" data-mp="3axfr6xsqrzj2">
      <div className="flex justify-end" data-id="element-59" data-mp="3awwhg4d8whg4">
        <button onClick={() => setEditing('new')} className="btn btn-primary" data-id="element-60" data-mp="3fvhho3shz14k"><PlusIcon className="h-4 w-4" aria-hidden data-id="element-61" data-mp="3fuo7sty8rvi7" /> Nuevo banner</button>
      </div>
      <ul className="mt-5 divide-y divide-line rounded-2xl border border-line" data-id="element-62" data-mp="315rzcvs9wgh2">
        {banners.map(b => {
        const live = b.active && isWithin(b.startsAt, b.endsAt);
        return <li key={b.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center" data-id="element-63" data-mp="2s4brovsuesgj">
              <img src={b.image} alt="" className="h-14 w-24 rounded object-cover" data-id="element-64" data-mp="2w89q14acfz8j" />
              <div className="min-w-0 flex-1" data-id="element-65" data-mp="2i8qwbgia4qfr">
                <p className="truncate font-semibold" data-id="element-66" data-mp="2ia1zbe3whu5t">{b.title}</p>
                <p className="text-xs text-mist" data-id="element-67" data-mp="2n7lfijk5sh0e">
                  {b.kind} · {formatDate(b.startsAt)} → {formatDate(b.endsAt)} · <span className={live ? 'text-ok' : 'text-mist'} data-id="element-68" data-mp="gh5dqxcd797g">{live ? 'En línea' : 'No visible'}</span>
                </p>
              </div>
              <div className="flex gap-2" data-id="element-69" data-mp="25wy8yp76buld">
                <button onClick={() => setEditing(b)} className="btn btn-ghost h-9" data-id="element-70" data-mp="25xziuqcgeb1i">Editar</button>
                <button onClick={() => window.confirm('¿Eliminar este banner?') && deleteBanner(b.id)} className="btn btn-danger h-9" data-id="element-71" data-mp="20zy4gipz2zwd">Eliminar</button>
              </div>
            </li>;
      })}
        {banners.length === 0 && <li className="p-8 text-center text-sm text-mist" data-id="element-72" data-mp="2auq2tordpqs7">No hay banners cargados.</li>}
      </ul>
      <Modal open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? 'Nuevo banner' : 'Editar banner'} data-id="element-73" data-mp="2opsfcuqqeedz">
        {editing !== null && <BannerEditor banner={editing === 'new' ? null : editing} onDone={() => setEditing(null)} data-id="element-74" data-mp="2yku3pod6ncfi" />}
      </Modal>
    </div>;
}